import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Instagram, ArrowUpRight, Truck } from "lucide-react";
import BackgroundWord from "./BackgroundWords";
import { useContent } from "../context/ContentContext";

export const Studios = () => {
  const { content } = useContent();
  const s = content?.studios || {};
  const handles = s.handles || [];

  return (
    <section
      id="studios"
      data-testid="studios-section"
      className="relative bg-[#070707] py-32 md:py-44 overflow-hidden grain"
    >
      <BackgroundWord word="Studios" position="right" speed={0.35} />

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full bg-[#ff0033]/10 blur-[140px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6 text-[11px] tracking-[0.4em] uppercase text-zinc-500 font-semibold">
              <span className="block w-10 h-px bg-[#ff0033] shadow-[0_0_10px_#ff0033]" />
              {s.eyebrow || "05 — Unsere Studios"}
            </div>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-6xl lg:text-7xl max-w-3xl">
              {s.title_a || "Coming Soon."} <br />
              <span className="text-zinc-500">{s.title_b || "Die Räume"} </span>
              <span className="text-glow-red text-white">{s.title_c || "entstehen."}</span>
            </h2>
          </div>
          <p className="text-zinc-500 text-sm max-w-sm leading-relaxed">
            {s.intro ||
              "Unser festes Studio ist im Aufbau. Bis der Standort steht, kommen wir mobil zu dir — komplettes Equipment, professionelle Sessions, direkt vor Ort."}
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="relative border border-white/[0.08] bg-[#0A0A0A] overflow-hidden"
          data-testid="studios-soon-panel"
        >
          <div className="absolute top-0 left-0 right-0 h-px hairline-red" />

          <div className="grid md:grid-cols-12 gap-0">
            <div className="md:col-span-5 p-8 md:p-14 border-b md:border-b-0 md:border-r border-white/[0.06] flex flex-col justify-between min-h-[280px] md:min-h-[360px] relative">
              <div>
                <div className="flex items-center gap-3 text-[10px] tracking-[0.4em] uppercase text-zinc-500 font-semibold">
                  <span className="red-led" />
                  {s.status_label || "Status · Aufbau"}
                </div>
                <div className="mt-8 font-display font-black uppercase text-5xl md:text-6xl leading-[0.9] tracking-tighter">
                  {s.big_word || "Build"} <br />
                  <span className="text-[#ff0033] text-glow-red">{s.build_year || "2026"}</span>
                </div>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-4">
                {[
                  { l: "Phase", v: s.phase || "01/03" },
                  { l: "Standort", v: s.location || "BLN" },
                  { l: "Launch", v: s.launch || "TBA" },
                ].map((m) => (
                  <div key={m.l} className="border-t border-white/10 pt-3" data-testid={`studio-meta-${m.l}`}>
                    <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-600">{m.l}</div>
                    <div className="font-display font-bold text-lg mt-1">{m.v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="md:col-span-7 p-8 md:p-14 flex flex-col justify-center">
              <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 font-semibold mb-4">
                {s.right_eyebrow || "Bis dahin · Mobile Studio"}
              </div>
              <h3 className="font-display font-black uppercase text-3xl md:text-4xl tracking-tight leading-tight">
                {s.right_title_a || "Wir kommen mit komplettem"} <span className="text-glow-red text-white">{s.right_title_b || "Equipment zu dir."}</span>
              </h3>
              <p className="mt-4 text-sm text-zinc-400 max-w-lg leading-relaxed">
                {s.right_body || "Solange unser Standort im Aufbau ist, packen wir alles ein und nehmen bei dir auf — Recording-Stack, Mics, Akustik-Setup. Sessions ab 49 €."}
              </p>

              <Link
                to="/studio"
                data-testid="studio-page-cta"
                className="mt-8 group relative inline-flex items-center gap-3 bg-white text-black px-6 py-4 text-[10px] md:text-[11px] font-bold tracking-[0.3em] uppercase overflow-hidden hover:shadow-[0_0_24px_rgba(255,0,51,0.4)] transition-all self-start"
              >
                <Truck size={14} className="relative z-10 group-hover:text-white transition-colors" />
                <span className="relative z-10 group-hover:text-white transition-colors">{s.cta_label || "Zum Mobilen Studio"}</span>
                <ArrowUpRight size={14} className="relative z-10 group-hover:text-white group-hover:rotate-45 transition-all duration-500" />
                <span className="absolute inset-0 bg-[#ff0033] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Instagram strip — slimmer, secondary */}
        {handles.length > 0 && (
          <div className="mt-12 grid md:grid-cols-2 gap-3" data-testid="studio-socials">
            {handles.map((h, i) => (
              <a
                key={h.tag}
                href={h.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`instagram-${i}`}
                className="group flex items-center justify-between border border-white/[0.06] bg-[#0A0A0A] px-5 py-4 hover:border-[#ff0033]/40 hover:shadow-[0_0_24px_rgba(255,0,51,0.15)] transition-all duration-500"
              >
                <div className="flex items-center gap-3">
                  <Instagram size={18} className="text-white group-hover:text-[#ff0033] transition-colors duration-300" />
                  <span className="font-display font-black uppercase tracking-tight text-base">{h.tag}</span>
                </div>
                <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-zinc-500 group-hover:text-white transition-colors">
                  <span className="hidden sm:inline">Folgen</span>
                  <ArrowUpRight size={14} className="text-[#ff0033] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-300" />
                </div>
              </a>
            ))}
          </div>
        )}

        <div className="mt-10 text-center text-[10px] tracking-[0.4em] uppercase text-zinc-600">
          · Updates in Echtzeit auf Instagram ·
        </div>
      </div>
    </section>
  );
};

export default Studios;
